import { useId } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { selectors } from 'darwin_core';

import { homeActionPath } from '../navigation/paths.js';
import {
  homeHolidayMessage,
  homeHolidayStatus,
  sameHomeHolidayStatus,
  zoneHolidayMessage,
  zoneHolidayStatus,
} from '../controls/holidayStatus.js';

// The Holiday Mode notice — the web's equivalent of the app's CancelHolidayHeader
// (title, "…in Holiday Mode until <date>. With this mode active…", CANCEL HOLIDAY).
// It exists because the web cannot make a screen unreachable: the app blocks a
// zone's tile while its thermostat is away, but a browser user can land on any
// screen by URL, bookmark or refresh, and would otherwise find controls greyed
// with nothing saying why. So every screen that can send a command renders one,
// and it names the way out — a link to the Holiday screen, whose Cancel Holiday
// ends the holiday for every thermostat that is away.
//
// Two scopes, because holiday is per thermostat (see holidayStatus.js):
//   • ZoneHolidayBanner — the zone screen and its action screens. Reads the zone's
//     OWN state and end, so in a two-zone home each screen is accurate and a zone
//     whose thermostat is not away shows nothing.
//   • HomeHolidayBanner — the Property screen. Says "your heating" when every
//     heating zone is away and names the zones when only some are.
//
// Each renders a SLOT that stays mounted whether or not there is a holiday, and
// the slot is the polite live region: a live region only announces changes made
// inside it after it is in the accessibility tree, so mounting the notice with the
// aria-live on it would announce nothing. With the slot always present, a holiday
// that starts from another client while the screen is open is read out. The slot
// collapses when empty (see .holiday-banner-slot:empty). `detail` is the screen's
// own sentence on what is blocked and how to unblock it; it defaults to the app's
// generic wording.

const DEFAULT_DETAIL =
  'With this mode active some settings will not be accessible.';

function HolidayNotice({ homeId, message, detail = DEFAULT_DETAIL }) {
  const titleId = useId();
  return (
    <section className="holiday-banner" aria-labelledby={titleId}>
      <h2 id={titleId} className="holiday-banner__title">
        Holiday Mode
      </h2>
      <p className="holiday-banner__text">
        {message} {detail}
      </p>
      <Link
        className="holiday-banner__cancel"
        to={homeActionPath(homeId, 'holiday')}
      >
        Cancel Holiday
      </Link>
    </section>
  );
}

function HolidaySlot({ children }) {
  return (
    <div className="holiday-banner-slot" aria-live="polite">
      {children}
    </div>
  );
}

export function ZoneHolidayBanner({ homeId, zoneId, detail }) {
  const { inHoliday, end } = useSelector(
    (state) => zoneHolidayStatus(state, homeId, zoneId),
    shallowEqual,
  );
  const name = useSelector((state) =>
    selectors.getPrtFriendlyName(state, homeId, zoneId),
  );

  return (
    <HolidaySlot>
      {inHoliday ? (
        <HolidayNotice
          homeId={homeId}
          message={zoneHolidayMessage(name, end)}
          detail={detail}
        />
      ) : null}
    </HolidaySlot>
  );
}

export function HomeHolidayBanner({ homeId, detail }) {
  const status = useSelector(
    (state) => homeHolidayStatus(state, homeId),
    sameHomeHolidayStatus,
  );

  return (
    <HolidaySlot>
      {status.away.length > 0 ? (
        <HolidayNotice
          homeId={homeId}
          message={homeHolidayMessage(status)}
          detail={detail}
        />
      ) : null}
    </HolidaySlot>
  );
}
